<template>
  <footer class="reg-footer" data-testid="reg-footer">
    <span class="reg-footer__left">
      <LeadButton
        v-if="canGoBack"
        variant="secondary-outline"
        :icon="IconArrowLeft"
        data-testid="reg-back"
        @click="emit('back')">
        {{ $t('registration.footer.back') }}
      </LeadButton>
      <span v-if="note" class="reg-footer__note">{{ note }}</span>
    </span>

    <span class="reg-footer__right">
      <LeadButton
        v-if="canSkip"
        variant="link"
        class="reg-footer__skip"
        :disabled="isSubmitting"
        data-testid="reg-skip"
        @click="emit('skip')">
        {{ $t('registration.footer.do_this_later') }}
      </LeadButton>
      <!-- submits the form so Enter and a click take the same path -->
      <LeadButton
        type="submit"
        :form="formId"
        variant="primary"
        :trailing-icon="IconArrowRight"
        :loading="isSubmitting"
        :disabled="!canGoNext"
        data-testid="reg-next">
        {{ nextLabel }}
      </LeadButton>
    </span>
  </footer>
</template>

<script setup lang="ts">
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-vue';

import LeadButton from '@/components/lead-design/LeadButton.vue';

interface Props {
  /** id of the form the primary action submits */
  formId: string;
  nextLabel: string;
  note: string;
  canGoBack: boolean;
  canGoNext: boolean;
  canSkip: boolean;
  isSubmitting?: boolean;
}

defineProps<Props>();

const emit = defineEmits<{ back: []; skip: [] }>();
</script>

<style scoped lang="scss">
@use '@/assets/registration' as reg;

.reg-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  // the DS button is 4px shorter than the sidebar support block; min-height keeps the top border aligned with it
  min-height: calc(2 * #{reg.$footer-padding-y} + 41px);
  padding: reg.$footer-padding-y 72px;
  border-top: 1px solid #edeeef;
  background: #fff;

  &__left,
  &__right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__right {
    gap: 20px;
  }

  &__note {
    font-size: 13px;
    line-height: 20px;
    color: #8e939d;
  }

  &__skip {
    padding: 0;
    font-size: 13px;
    line-height: 20px;
    font-weight: 500;
    white-space: nowrap;
  }
}

@media (max-width: 1199.98px) {
  .reg-footer {
    gap: 12px;
    min-height: calc(2 * #{reg.$footer-padding-y-tablet} + 41px);
    padding: reg.$footer-padding-y-tablet 32px;

    // the note is the first thing to drop when space is tight
    &__note {
      display: none;
    }
  }
}

@media (max-width: 767.98px) {
  .reg-footer {
    padding: 12px 16px;
  }
}
</style>
